import { Controller } from "@hotwired/stimulus";

const DEFAULT_DEBOUNCE_MS = 600;
// Minimum time the "Saving…" state stays on screen. A save that returns
// almost instantly would otherwise be an imperceptible flash.
const DEFAULT_MIN_SAVING_MS = 1000;

/**
 * Auto-saves a form in the background. Listens for `input`/`change` anywhere
 * inside its element, debounces, and submits the form with `autosave=1` via a
 * plain `fetch` (not a Turbo submit) — the server replies `head :ok` /
 * `head :unprocessable_content` quietly, with no flash toast.
 *
 * Save progress is broadcast as a window event (`{ detail: { state } }`) so a
 * `save-indicator` controller elsewhere on the page can reflect it. This
 * controller renders nothing itself. The event name defaults to `survey-save`;
 * set `eventValue` to keep a different surface on its own channel.
 *
 * Mount it directly on a `<form>`, or on a wrapper element that contains a
 * `form` target. Optional `blockSelector` value: while an element matching
 * that selector exists and is visible (not `.hidden`), saves are skipped —
 * used by the scoring page to avoid PATCHing a config the model will reject
 * while the band thresholds are invalid.
 */
export default class extends Controller<HTMLElement> {
  static targets = ["form"];
  static values = {
    debounce: { type: Number, default: DEFAULT_DEBOUNCE_MS },
    blockSelector: { type: String, default: "" },
    minSaving: { type: Number, default: DEFAULT_MIN_SAVING_MS },
    event: { type: String, default: "survey-save" },
  };

  declare readonly formTarget: HTMLFormElement;
  declare readonly hasFormTarget: boolean;
  declare readonly debounceValue: number;
  declare readonly blockSelectorValue: string;
  declare readonly minSavingValue: number;
  declare readonly eventValue: string;

  private debounceTimer: number | null = null;
  private boundSchedule = () => this.schedule();

  connect() {
    this.element.addEventListener("input", this.boundSchedule);
    this.element.addEventListener("change", this.boundSchedule);
  }

  disconnect() {
    this.element.removeEventListener("input", this.boundSchedule);
    this.element.removeEventListener("change", this.boundSchedule);
    if (this.debounceTimer !== null) window.clearTimeout(this.debounceTimer);
  }

  // Public so buttons that fire `click` (not `change`) can trigger a save
  // via `data-action="form-auto-save#schedule"`.
  schedule() {
    if (this.debounceTimer !== null) window.clearTimeout(this.debounceTimer);
    this.debounceTimer = window.setTimeout(() => {
      this.debounceTimer = null;
      void this.save();
    }, this.debounceValue);
  }

  // Public for tests.
  async save() {
    const form = this.formObject();
    if (!form) return;

    if (this.blocked()) {
      this.broadcast("idle");
      return;
    }

    this.broadcast("saving");
    const startedAt = Date.now();

    const body = new FormData(form);
    body.set("autosave", "1");

    let state: "saved" | "error";
    try {
      const response = await fetch(form.action, {
        method: "POST",
        body,
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      state = response.ok ? "saved" : "error";
    } catch {
      state = "error";
    }

    // Hold "Saving…" for a minimum so a near-instant save still registers
    // as a deliberate state change rather than a flicker.
    const remaining = this.minSavingValue - (Date.now() - startedAt);
    if (remaining > 0) {
      await new Promise((resolve) => window.setTimeout(resolve, remaining));
    }

    this.broadcast(state);
  }

  private formObject(): HTMLFormElement | null {
    if (this.hasFormTarget) return this.formTarget;
    if (this.element instanceof HTMLFormElement) return this.element;
    return null;
  }

  private blocked(): boolean {
    if (this.blockSelectorValue === "") return false;
    const el = this.element.querySelector<HTMLElement>(this.blockSelectorValue);
    return !!el && !el.classList.contains("hidden");
  }

  private broadcast(state: "idle" | "saving" | "saved" | "error") {
    window.dispatchEvent(new CustomEvent(this.eventValue, { detail: { state } }));
  }
}
